import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Bottom, Main, Top, Title, Time, Overlay, MoreIcon } from './styles';
import { captureException } from '../../services/logService';
import copy from 'clipboard-copy';
import { deleteSnap, dispatchSnapSuccess } from '../../services/snapService';
import Modal from '../modals/modal';
import DeleteModal from '../modals/deleteModal';
import SnapRenameModal from '../modals/snapRenameModal';
import { Dropdown, DropdownItem } from '../dropdown';
import { ScreenRecording } from '../../mobx/interfaces/app';
import { deleteFileFromFirebase } from '../../helpers/firebaseHelper';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../global/constants';
import IconButton from '../button/iconButton';

interface Props {
  time: string;
  loadData: () => void;
  snapData: ScreenRecording;
  selectedItem: string | null;
  setSelectedItem: (value: string | null) => void;
}
const SnapCardView: React.FC<Props> = (props) => {
  const { snapData, selectedItem, setSelectedItem } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  //State
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };

    const currentVideoRef = videoRef.current;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVideoVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    if (currentVideoRef) {
      observer.observe(currentVideoRef);
    }

    return () => {
      if (currentVideoRef) {
        observer.unobserve(currentVideoRef);
      }
    };
  }, []);

  const onCopyLink = useCallback(
    async (shareId: string) => {
      try {
        setSelectedItem(null);
        await copy(`https://share.teamcamp.app/${shareId}`);
        dispatchSnapSuccess('Screen recording url copied successfully!');
      } catch (error) {
        captureException(error);
      }
    },
    [setSelectedItem]
  );

  /**
   * Handles the click event for downloading a file.
   * @param {string} fileUrl - The URL of the file to be downloaded.
   * @param {string} fileName - The name of the file to be downloaded.
   */
  const handleDownloadClick = useCallback(
    (fileUrl: string, fileName: string) => {
      setSelectedItem(null);
      fetch(fileUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = blobUrl;
          link.setAttribute('download', fileName);

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch((error) => {
          console.error('Error downloading file:', error);
        });
    },
    [setSelectedItem]
  );

  const openDeleteModel = useCallback(() => {
    setSelectedItem(null);
    setIsDeleteModelOpen(true);
  }, [setSelectedItem]);

  const closeDeleteModel = useCallback(() => {
    setIsDeleteModelOpen(false);
  }, []);

  const onClickRename = useCallback(() => {
    setSelectedItem(null);
    setIsOpen(true);
  }, [setSelectedItem]);

  const cancel = () => {
    setIsOpen(false);
  };

  const onSnapDelete = useCallback(
    async (snapId: string, ref: string) => {
      setLoading(true);
      try {
        const url = new URL(ref);
        const decodedFilename = decodeURIComponent(url.pathname);
        const parts = decodedFilename?.split('/');
        const firebasePath = parts?.slice(-4);
        const filePath = firebasePath.join('/');
        await deleteSnap(snapId);
        await deleteFileFromFirebase(filePath);
        dispatchSnapSuccess('Screen recording deleted successfully!');
      } catch (error) {
        console.log('error in deleting snap', error);
      } finally {
        setLoading(false);
        setIsDeleteModelOpen(false);
        props.loadData();
      }
    },
    [props]
  );

  const onClickCard = useCallback((shareId: string) => {
    const newTab = window.open(`https://share.teamcamp.app/${shareId}`, '_blank');
    if (newTab) {
      newTab.focus();
    } else {
      alert('Please allow pop-ups for this site to open in a new tab.');
    }
  }, []);

  return (
    <>
      <Main role='button' isActive={snapData['_id'] === selectedItem} onClick={() => onClickCard(snapData?.['_id'])}>
        <Top>
          <video ref={videoRef} preload='auto' playsInline>
            {isVideoVisible && (
              <>
                <source type='video/mp4' src={snapData?.Ref} />
                <source type='video/webm' src={snapData?.Ref?.replace(/\.mp4$/i, '.webm')} />
              </>
            )}
          </video>
          <Overlay className='overlay'></Overlay>
          <MoreIcon>
            <Dropdown
              activeClassName='active'
              content={
                <IconButton
                  iconName='three-dots-horizontal-icon'
                  size={ICON_BUTTON_SIZE.SMALL}
                  variant={ICON_BUTTON_TYPE.TERTIARY}
                  onClick={() => setSelectedItem(snapData['_id'])}
                  className='more-icon'
                />
              }
              trigger='click'
              onOutsideClick={() => setSelectedItem(null)}>
              <DropdownItem key={1} label='Copy link' onClick={() => onCopyLink(snapData?.['_id'])} />
              <DropdownItem key={2} label='Rename' onClick={onClickRename} />
              <DropdownItem
                key={3}
                label='Download'
                onClick={() => handleDownloadClick(snapData?.Ref, snapData?.Name)}
              />
              <DropdownItem key={4} label='Delete' onClick={openDeleteModel} tone='critical' />
            </Dropdown>
          </MoreIcon>
        </Top>
        <Bottom>
          <Title>{snapData?.Name}</Title>
          <Time>{props?.time}</Time>
        </Bottom>
      </Main>
      <Modal open={isDeleteModelOpen} onClose={closeDeleteModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeDeleteModel}
          loading={loading}
          onOk={() => onSnapDelete(snapData['_id'], snapData?.Ref)}
          modalTitle='Delete Snap?'
          description='Are you sure you want to delete this snap?'
        />
      </Modal>
      <Modal open={isOpen} onClose={cancel} maxWidth={'334px'}>
        <SnapRenameModal onClose={cancel} loadData={props.loadData} snapData={props?.snapData} />
      </Modal>
    </>
  );
};
export default SnapCardView;
